import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { UnitsCard, PropertyCardData } from '../../units-card/units-card';
import { ApiProperty } from '../../../../core/models/api-property.model';

@Component({
  selector: 'app-similar-properties',
  standalone: true,
  imports: [CommonModule, UnitsCard],
  template: `
    <div class="surface-card p-6 md:p-8 rounded-2xl shadow-sm border border-border">
      <h2 class="text-text-primary text-xl font-black mb-6">عقارات مشابهة</h2>

      <!-- Loading -->
      @if (loading) {
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          @for (n of [1, 2, 3]; track n) {
            <div class="rounded-xl border border-border-subtle bg-bg-secondary overflow-hidden animate-pulse">
              <div class="h-44 bg-surface-elevated"></div>
              <div class="p-4 space-y-3">
                <div class="h-4 bg-surface-elevated rounded w-3/4"></div>
                <div class="h-3 bg-border-subtle rounded w-1/2"></div>
                <div class="h-5 bg-surface-elevated rounded w-1/3 mt-2"></div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Units -->
      @if (!loading && units.length > 0) {
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          @for (unit of units; track unit._id) {
            <app-units-card [property]="unit" (propertyClick)="onUnitClick($event)">
            </app-units-card>
          }
        </div>
      }

      @if (!loading && units.length === 0) {
        <div
          class="w-full bg-bg-secondary/40 border border-border-subtle rounded-3xl p-8 sm:p-12 shadow-inner text-center antialiased relative overflow-hidden animate-fadeIn"
        >
          <div
            class="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(228,87,5,0.05)_0%,transparent_70%)]"
          ></div>

          <div class="relative z-10 max-w-sm mx-auto flex flex-col items-center">
            <div class="flex items-center justify-center -space-x-4 mb-6">
              <div
                class="w-12 h-16 rounded-xl border border-border bg-surface-elevated rotate-[-8deg] transform origin-bottom opacity-40 flex items-center justify-center text-text-muted"
              >
                <i class="fa-solid fa-house text-xs"></i>
              </div>

              <div
                class="w-14 h-18 rounded-2xl bg-surface-elevated border border-primary/20 shadow-[0_10px_25px_-5px_rgba(228,87,5,0.15)] flex flex-col items-center justify-center text-primary relative z-10 p-2"
              >
                <div class="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center mb-1">
                  <i class="fa-solid fa-building-circle-exclamation text-xs"></i>
                </div>
                <span class="w-4 h-1 bg-border rounded-full mt-1"></span>
              </div>

              <div
                class="w-12 h-16 rounded-xl border border-border bg-surface-elevated rotate-[8deg] transform origin-bottom opacity-40 flex items-center justify-center text-text-muted"
              >
                <i class="fa-solid fa-house text-xs"></i>
              </div>
            </div>

            <div class="space-y-1.5 font-sans">
              <h3 class="text-sm sm:text-base font-black text-text-primary tracking-tight">
                لا توجد وحدات مشابهة مدرجة بعد
              </h3>
              <p class="text-xs font-medium text-text-secondary leading-relaxed max-w-xs">
                لا توجد عقارات مطابقة لهذه المواصفات في الوقت الحالي.
                سيتم إدراج الإعلانات المشابهة هنا تلقائيًا.
              </p>
            </div>

            <div
              class="mt-5 inline-flex items-center gap-1.5 px-3 py-1 bg-surface-elevated border border-border-subtle rounded-full text-[9px] font-black uppercase tracking-widest font-mono text-text-muted shadow-sm"
            >
              <span class="w-1 h-1 rounded-full bg-warning animate-pulse"></span>
              <span>مراقبة المزامنة التلقائية نشطة</span>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class SimilarPropertiesComponent implements OnInit, OnChanges {
  @Input() purpose: 'sale' | 'rent' = 'sale';
  @Input() excludeId: string = '';

  units: ApiProperty[] = [];
  loading = false;

  private http = inject(HttpClient);
  private router = inject(Router);
  private readonly baseUrl = environment.api.baseUrl;

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['purpose'] || changes['excludeId']) {
      this.load();
    }
  }

  private load(): void {
    this.loading = true;
    this.units = [];

    const params: Record<string, string> = {
      purpose: this.purpose,
      limit: '6',
      page: '1',
    };

    const query = new URLSearchParams(params).toString();

    this.http
      .get<{
        results?: { data?: ApiProperty[] };
        data?: ApiProperty[];
      }>(`${this.baseUrl}/public/search/units-and-properties?${query}`)
      .subscribe({
        next: (res) => {
          const data: ApiProperty[] = res?.results?.data ?? (res?.data as ApiProperty[]) ?? [];
          // exclude current unit
          this.units = data
            .filter((u) => u._id !== this.excludeId && u.targetId !== this.excludeId)
            .slice(0, 3);
          this.loading = false;
        },
        error: () => {
          this.units = [];
          this.loading = false;
        },
      });
  }

  onUnitClick(property: PropertyCardData): void {
    const prop = property as ApiProperty;
    const id = prop.targetId || prop._id;
    if (!id) return;

    const route = this.purpose === 'rent' ? '/rent/unit' : '/buy/unit';
    this.router.navigate([route, id], { state: { property } });
  }
}
